import { useEffect } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_GlobalFilterTextField,
} from "material-react-table";
import {
  Box, Typography, IconButton, Button, Select, MenuItem, Tooltip, Skeleton,
} from "@mui/material";
import {
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
} from "@mui/icons-material";

const defaultPagination = {
  pageIndex: 0,
  pageSize: 10,
};

const MRTTable = ({
  data,
  columns,
  renderRowActions,
  renderTopToolbarCustomActions,
  pageSize = 5,
  loading,
  pagination = defaultPagination,
  onPaginationChange,
  rowCount,
  manualPagination,
  enableRowSelection,
  onSelectionChange,
  expanded,
  ...others
}) => {
  const totalCount = rowCount ?? data.length;
  const currentPage = pagination.pageIndex;
  const currentPageSize = pagination.pageSize;
  const totalPages = Math.ceil(totalCount / currentPageSize) || 1;

  // MRT only shows skeleton cells inside existing rows - if data is empty
  // (initial load) it renders "No records to display" instead. Feed it
  // placeholder rows while loading so the skeleton is actually visible.
  const isSkeletonLoad = loading && data.length === 0;
  const tableData = isSkeletonLoad
    ? Array.from({ length: pagination.pageSize || pageSize }, (_, i) => ({ id: `skeleton-${i}`, __skeleton: true }))
    : data;

  // Columns can define their own Cell (chips, badges, etc.) which would
  // otherwise render fallback text instead of a skeleton for placeholder
  // rows. Force a skeleton bar for every column while placeholder rows
  // are showing, regardless of the column's own Cell renderer.
  const skeletonColumns = isSkeletonLoad
    ? columns.map((col) => ({
        ...col,
        Cell: () => (
          <Skeleton
            animation="wave"
            height={22}
            sx={{ borderRadius: "6px", bgcolor: (theme) => theme.palette.mode === "light" ? "grey.200" : "grey.700" }}
          />
        ),
      }))
    : columns;

  const handlePageChange = (newPage) => {
    if (onPaginationChange) {
      onPaginationChange((prev) => ({ ...prev, pageIndex: newPage }));
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    if (onPaginationChange) {
      onPaginationChange({ pageIndex: 0, pageSize: newSize });
    }
  };

  const table = useMaterialReactTable({
    columns: skeletonColumns,
    data: tableData,
    enableColumnActions: true,
    enableRowActions: true,
    positionActionsColumn: "last",
    enableDensityToggle: false,
    enableColumnPinning: true,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    enableDensityToggle: true,
    enableRowSelection: enableRowSelection || false,
    enableFullScreenToggle: false,
    layoutMode: "semantic",
    enableStickyHeader: true,
    rowPinningDisplayMode: "select-sticky",
    displayColumnDefOptions: { "mrt-row-actions": { size: 80 } },

    initialState: {
      density: "comfortable",
      showGlobalFilter: true,
      columnPinning: { left: [], right: ["mrt-row-actions"] },
      expanded: expanded || false,
    },
    manualPagination: manualPagination || false,
    onPaginationChange: onPaginationChange,
    rowCount: totalCount,
    enablePagination: true,
    paginationDisplayMode: "custom",
    state: { showSkeletons: loading, showProgressBars: loading, pagination },
    muiSkeletonProps: {
      animation: "wave",
      height: 22,
      sx: {
        borderRadius: "6px",
        bgcolor: (theme) =>
          theme.palette.mode === "light" ? "grey.200" : "grey.700",
      },
    },

    muiTableHeadRowProps: {
      sx: {
        background: (theme) =>
          `${
            theme.palette.mode === "light"
              ? theme.palette.grey[200]
              : theme.palette.grey[700]
          }  !important`,
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      "data-testid": `row-${row.id}`,
      sx: {
        "&.MuiTableRow-root.Mui-selected .MuiTableCell-body": {
          background: (theme) => theme.palette.primary.light,
          color: "black",
        },
      },
    }),
    muiTableContainerProps: {
      sx: {
        maxHeight: "none",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
      },
    },
    muiPaginationProps: {
      sx: {
        "& .MuiToolbar-root": {
          justifyContent: "flex-start",
          paddingLeft: 0,
        },
      },
    },
    muiTopToolbarProps: {
      sx: {
        justifyContent: "flex-start",
        gap: 2,
      },
    },
    muiBottomToolbarProps: {
      sx: {
        justifyContent: "flex-start",
        gap: 2,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        color: (theme) =>
          theme.palette.mode === "light"
            ? theme.palette.grey[600]
            : theme.palette.grey[200],
        paddingLeft: "4px",
        paddingRight: "8px",
        fontWeight: 600,
        boxShadow: "none",
        background: (theme) =>
          `${
            theme.palette.mode === "light"
              ? theme.palette.grey[200]
              : theme.palette.grey[700]
          }  !important`,
        "& .Mui-TableHeadCell-Content": {
          justifyContent: "space-between",
        },
        "& .Mui-TableHeadCell-Content-Labels": {
          whiteSpace: "nowrap",
          overflow: "visible",
          textOverflow: "unset",
        },
        "& .MuiTableSortLabel-root": {
          whiteSpace: "nowrap",
          overflow: "visible",
        },
        whiteSpace: "nowrap",
        overflow: "visible",
        textOverflow: "unset",
        minWidth: "fit-content",
      },
    },
    muiTableBodyCellProps: {
      sx: { paddingLeft: "4px", paddingRight: "8px", boxShadow: "none" },
    },
    muiTableHeadCellColumnActionsButtonProps: {
      sx: {
        color: (theme) => theme.palette.primary.contrastText,
        opacity: 1,
      },
    },

    renderRowActions: isSkeletonLoad
      ? () => <Skeleton variant="circular" width={24} height={24} sx={{ mx: "auto" }} />
      : renderRowActions,
    renderTopToolbarCustomActions: renderTopToolbarCustomActions,

    muiDetailPanelProps: {
      sx: {
        display: "block",
        width: "100%",
      },
    },

    renderTopToolbar: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
          px: { xs: 1, sm: 2 },
          py: 1,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ flexShrink: 0, minWidth: 200, maxWidth: 280, flex: "1 1 200px" }}>
          <MRT_GlobalFilterTextField table={table} />
        </Box>
        {renderTopToolbarCustomActions && (
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, flex: "1 1 auto" }}>
            {renderTopToolbarCustomActions({ table })}
          </Box>
        )}
      </Box>
    ),

    renderBottomToolbar: () => {
      const btnSx = {
        minWidth: 32, height: 32, p: 0, fontSize: "0.78rem", fontWeight: 600,
        borderRadius: 1, border: "1px solid #e5e7eb",
      };
      const iconBtnSx = {
        width: 32, height: 32, borderRadius: 1,
        border: "1px solid #e5e7eb", color: "text.secondary",
      };

      // Build page window (max 5)
      const delta = 2;
      const range = [];
      for (
        let i = Math.max(0, currentPage - delta);
        i <= Math.min(totalPages - 1, currentPage + delta);
        i++
      ) {
        range.push(i);
      }

      const from = totalCount > 0 ? currentPage * currentPageSize + 1 : 0;
      const to = Math.min((currentPage + 1) * currentPageSize, totalCount);

      return (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 2,
            py: 1,
            borderTop: "1px solid #e5e7eb",
          }}
        >
          {/* Left: count + rows per page */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
              {totalCount > 0 ? `Showing ${from}–${to} of ${totalCount}` : "No entries"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontSize: "0.78rem", color: "text.secondary", whiteSpace: "nowrap" }}>
                Rows:
              </Typography>
              <Select
                size="small"
                value={currentPageSize}
                onChange={handlePageSizeChange}
                sx={{ fontSize: "0.78rem", height: 28, ".MuiSelect-select": { py: "3px", px: "8px" } }}
              >
                {[5, 10, 25, 50].map((n) => (
                  <MenuItem key={n} value={n} sx={{ fontSize: "0.78rem" }}>{n}</MenuItem>
                ))}
              </Select>
            </Box>
          </Box>

          {/* Right: First / Prev / pages / Next / Last */}
          {totalCount > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              <Tooltip title="First page">
                <span>
                  <IconButton size="small" onClick={() => handlePageChange(0)} disabled={currentPage === 0} sx={iconBtnSx}>
                    <FirstPageIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Previous page">
                <span>
                  <IconButton size="small" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} sx={iconBtnSx}>
                    <PrevIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>

              {range[0] > 0 && (
                <>
                  <Button variant="outlined" size="small" onClick={() => handlePageChange(0)} sx={btnSx}>1</Button>
                  {range[0] > 1 && <Typography sx={{ px: 0.5, fontSize: "0.78rem", color: "text.secondary" }}>…</Typography>}
                </>
              )}

              {range.map((p) => (
                <Button
                  key={p}
                  variant={p === currentPage ? "contained" : "outlined"}
                  size="small"
                  onClick={() => handlePageChange(p)}
                  sx={{
                    ...btnSx,
                    ...(p === currentPage ? {
                      backgroundColor: "#1a1a1a", color: "#fff", borderColor: "#1a1a1a",
                      "&:hover": { backgroundColor: "#333" },
                    } : {}),
                  }}
                >
                  {p + 1}
                </Button>
              ))}

              {range[range.length - 1] < totalPages - 1 && (
                <>
                  {range[range.length - 1] < totalPages - 2 && (
                    <Typography sx={{ px: 0.5, fontSize: "0.78rem", color: "text.secondary" }}>…</Typography>
                  )}
                  <Button variant="outlined" size="small" onClick={() => handlePageChange(totalPages - 1)} sx={btnSx}>
                    {totalPages}
                  </Button>
                </>
              )}

              <Tooltip title="Next page">
                <span>
                  <IconButton size="small" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} sx={iconBtnSx}>
                    <NextIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Last page">
                <span>
                  <IconButton size="small" onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages - 1} sx={iconBtnSx}>
                    <LastPageIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
      );
    },

    ...others,
  });

  useEffect(() => {
    if (!others.getRowId && !others.onRowSelectionChange) {
      table.resetRowSelection();
    }
  }, [data]);

  const rowSelectionState = table.getState().rowSelection;

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(table.getSelectedRowModel().flatRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSelectionChange, rowSelectionState]);

  return <MaterialReactTable id="mrt-table" table={table} />;
};

export default MRTTable;